// @flow

/**
 * Google Consent Mode v2 wiring.
 *
 * business.999.md ships Google Tag Manager (`GTM_CONTAINER_ID`), and this module
 * owns both halves of its consent wiring: the `gtag('consent', …)` signals the
 * container reads, and the loader that brings the container in.
 *
 * The default-deny snippet that must run BEFORE anything Google lives inline in
 * public/index.html (search for "Google Consent Mode v2") — a CRA bundle loads
 * far too late to precede it. Keep the two in sync.
 *
 * GTM is PRIOR-BLOCKED (CS9-537): the audit of 09.09.2026 found the container
 * loading before any user decision, which means any tag added to it would fire
 * unconsented. It is now injected by `loadGtm()` from buildConfig's onConsent /
 * onChange — i.e. only once a choice exists, accept or reject. After that point
 * Consent Mode governs exactly as before, so a user who rejects still yields
 * GA's cookieless ping and nothing else.
 *
 * Category → Google signal mapping:
 *   analytics       → analytics_storage
 *   functionality   → functionality_storage, personalization_storage
 *   necessary       → security_storage stays granted
 *
 * ⚠️ There is no `advertising` category on this app: nothing in this repository
 * loads an ad network, so ad_storage / ad_user_data / ad_personalization stay
 * DENIED permanently. The GTM container itself is not auditable from here — if
 * it ever ships Google Ads / remarketing tags, add an `advertising` category
 * (constants.js + cookieTables.js + buildConfig.js) and map it here, exactly as
 * 999-web-app does.
 */

import { GTM_CONTAINER_ID } from './constants';

type ConsentState = 'denied' | 'granted';

/** Guards `loadGtm` — the container must never be injected twice. */
let gtmInjected = false;

/** Ensure a gtag() shim exists, then return it. */
function getGtag(): Function {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }
  return window.gtag;
}

/**
 * Inject the GTM container. Call ONLY once the user has made a banner choice,
 * and always AFTER `applyConsentMode` has pushed the matching update — dataLayer
 * is a queue, so the container replays default → update before any tag runs.
 *
 * Idempotent: onConsent fires on every page load with valid consent and onChange
 * on every later preference change; the container must be injected once.
 */
export function loadGtm(): void {
  if (typeof document === 'undefined' || gtmInjected) return;
  const head = document.head || document.getElementsByTagName('head')[0];
  if (!head) return;
  gtmInjected = true;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;
  head.appendChild(script);
}

/**
 * Translate the accepted CMP categories into a Consent Mode v2 update and push it
 * to gtag. Safe to call on every page load and on every change.
 */
export function applyConsentMode(acceptedCategories: string[]): void {
  if (typeof window === 'undefined') return;

  const has = (category: string) => acceptedCategories.includes(category);

  const analytics: ConsentState = has('analytics') ? 'granted' : 'denied';
  const functionality: ConsentState = has('functionality')
    ? 'granted'
    : 'denied';

  getGtag()('consent', 'update', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: analytics,
    functionality_storage: functionality,
    personalization_storage: functionality,
    security_storage: 'granted',
  });
}
